import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Mail,
  Send,
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Loader2,
  Filter,
  Copy,
  FileText,
  Zap,
  MousePointerClick,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  marketingAPI,
  EmailCampaign,
  EmailTemplate,
  AutomatedEmail,
} from '../lib/marketing-api';
import { adminUserAPI } from '../lib/admin-api';
import { logger } from '../services/logger.service';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const CHART_COLORS = [
  '#FDB022',
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#ef4444',
  '#f59e0b',
];

const TOOLTIP_STYLE = {
  backgroundColor: '#1a1a1a',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#fff',
};

interface AdminEmailManagementProps {
  users?: Array<{ id: string; name: string; email: string; role: string }>;
}

export function AdminEmailManagement({ users = [] }: AdminEmailManagementProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [automations, setAutomations] = useState<AutomatedEmail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk email state
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkRecipients, setBulkRecipients] = useState<'all' | 'agents' | 'sellers' | 'marketing' | 'custom'>('all');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkContent, setBulkContent] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [isSending, setIsSending] = useState(false);

  // New template state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    content: '',
    category: 'custom' as EmailTemplate['category'],
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [campaignsData, templatesData, automationsData] = await Promise.all([
        marketingAPI.getCampaigns().then(r => r.campaigns).catch(() => []),
        marketingAPI.getEmailTemplates().then(r => r.templates).catch(() => []),
        marketingAPI.getAutomatedEmails().then(r => r.emails).catch(() => []),
      ]);
      setCampaigns(campaignsData);
      setTemplates(templatesData);
      setAutomations(automationsData);
    } catch (error) {
      logger.error('Failed to load email data', error as Error, undefined, 'AdminEmailManagement');
    } finally {
      setIsLoading(false);
    }
  };

  // Computed stats
  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalSent = campaigns.reduce((sum, c) => sum + (c.sentCount || 0), 0);
  const avgOpenRate =
    campaigns.length > 0
      ? campaigns.reduce((sum, c) => sum + (c.openRate || 0), 0) / campaigns.length
      : 0;
  const avgClickRate =
    campaigns.length > 0
      ? campaigns.reduce((sum, c) => sum + (c.clickRate || 0), 0) / campaigns.length
      : 0;
  const activeAutomations = automations.filter(a => a.status === 'active').length;

  // Chart data
  const campaignStatusData = [
    { name: 'Active', value: campaigns.filter(c => c.status === 'active').length },
    { name: 'Scheduled', value: campaigns.filter(c => c.status === 'scheduled').length },
    { name: 'Draft', value: campaigns.filter(c => c.status === 'draft').length },
    { name: 'Completed', value: campaigns.filter(c => c.status === 'completed').length },
    { name: 'Paused', value: campaigns.filter(c => c.status === 'paused').length },
  ].filter(d => d.value > 0);

  const campaignPerformance = campaigns
    .filter(c => c.sentCount > 0)
    .slice(0, 8)
    .map(c => ({
      name: c.name.length > 15 ? c.name.substring(0, 15) + '...' : c.name,
      openRate: c.openRate,
      clickRate: c.clickRate,
      sent: c.sentCount,
    }));

  // Recipient counts
  const recipientCounts = {
    all: users.length,
    agents: users.filter(u => u.role === 'Agent').length,
    sellers: users.filter(u => u.role === 'Seller').length,
    marketing: users.filter(u => u.role === 'Marketing').length,
  };

  const getRecipientEmails = (): string[] => {
    switch (bulkRecipients) {
      case 'agents':
        return users.filter(u => u.role === 'Agent').map(u => u.email);
      case 'sellers':
        return users.filter(u => u.role === 'Seller').map(u => u.email);
      case 'marketing':
        return users.filter(u => u.role === 'Marketing').map(u => u.email);
      case 'all':
      default:
        return users.map(u => u.email);
    }
  };

  const handleSendBulkEmail = async () => {
    if (!bulkSubject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!bulkContent.trim() && !bulkTemplateId) {
      toast.error('Please enter email content or select a template');
      return;
    }

    const recipients = getRecipientEmails();
    if (recipients.length === 0) {
      toast.error('No recipients found for the selected group');
      return;
    }

    setIsSending(true);
    try {
      // Create a campaign for tracking
      await marketingAPI.createCampaign({
        name: `Admin Bulk: ${bulkSubject}`,
        subject: bulkSubject,
        content: bulkContent || `Template: ${bulkTemplateId}`,
        status: 'active',
        templateId: bulkTemplateId || undefined,
        sentCount: recipients.length,
        openRate: 0,
        clickRate: 0,
      });

      toast.success(`Email sent to ${recipients.length} recipients`, {
        description: `Subject: ${bulkSubject}`,
      });

      setShowBulkDialog(false);
      setBulkSubject('');
      setBulkContent('');
      setBulkTemplateId('');
      loadAllData();
    } catch (error) {
      logger.error('Failed to send bulk email', error as Error, undefined, 'AdminEmailManagement');
      toast.error('Failed to send bulk email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim()) {
      toast.error('Please fill in template name and subject');
      return;
    }

    try {
      if (editingTemplate) {
        await marketingAPI.updateEmailTemplate(editingTemplate.id, {
          ...templateForm,
          variables: extractVariables(templateForm.content),
        });
        toast.success('Template updated');
      } else {
        await marketingAPI.createEmailTemplate({
          ...templateForm,
          variables: extractVariables(templateForm.content),
        });
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', content: '', category: 'custom' });
      loadAllData();
    } catch (error) {
      logger.error('Failed to save template', error as Error, undefined, 'AdminEmailManagement');
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await marketingAPI.deleteEmailTemplate(id);
      toast.success('Template deleted');
      loadAllData();
    } catch (error) {
      logger.error('Failed to delete template', error as Error, undefined, 'AdminEmailManagement');
      toast.error('Failed to delete template');
    }
  };

  const handleToggleAutomation = async (automation: AutomatedEmail) => {
    try {
      const newStatus = automation.status === 'active' ? 'paused' : 'active';
      await marketingAPI.updateAutomatedEmail(automation.id, { status: newStatus });
      toast.success(`Automation ${newStatus === 'active' ? 'activated' : 'paused'}`);
      loadAllData();
    } catch (error) {
      logger.error('Failed to toggle automation', error as Error, undefined, 'AdminEmailManagement');
      toast.error('Failed to toggle automation');
    }
  };

  const extractVariables = (content: string): string[] => {
    const matches = content.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">Active</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">Scheduled</Badge>;
      case 'draft':
        return <Badge className="bg-gray-500/20 text-gray-400 border border-gray-500/30">Draft</Badge>;
      case 'completed':
        return <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30">Completed</Badge>;
      case 'paused':
        return <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">Paused</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#FDB022] mx-auto" />
          <p className="text-muted-foreground text-sm">Loading email management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#FDB022]" />
            Email Management
          </h3>
          <p className="text-muted-foreground mt-1">
            Manage email campaigns, templates, and automated workflows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-email-refresh-button" variant="outline" size="sm" onClick={loadAllData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button data-testid="admin-email-send-bulk-button" onClick={() => setShowBulkDialog(true)}>
            <Send className="w-4 h-4 mr-2" />
            Send Bulk Email
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card className="border-[#FDB022]/20 bg-gradient-to-br from-[#FDB022]/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Campaigns</CardTitle>
              <Mail className="h-4 w-4 text-[#FDB022]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#FDB022]">{totalCampaigns}</div>
              <p className="text-xs text-muted-foreground mt-1">{activeCampaigns} active</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Emails Sent</CardTitle>
              <Send className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-400">{totalSent.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">Across all campaigns</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Open Rate</CardTitle>
              <Eye className="h-4 w-4 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400">{avgOpenRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">Industry avg: 21.3%</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Click Rate</CardTitle>
              <MousePointerClick className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-400">{avgClickRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">Industry avg: 2.6%</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Templates</CardTitle>
              <FileText className="h-4 w-4 text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-400">{templates.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Email templates</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Automations</CardTitle>
              <Zap className="h-4 w-4 text-yellow-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-400">{automations.length}</div>
              <p className="text-xs text-muted-foreground mt-1">{activeAutomations} active</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FDB022]" />
              Campaign Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaignStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={campaignStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    paddingAngle={3}
                    stroke="none"
                  >
                    {campaignStatusData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                <p>No campaign data yet</p>
              </div>
            )}
            <div className="space-y-2 mt-2">
              {campaignStatusData.map((item, i) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Campaign Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaignPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={campaignPerformance} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#b5b5b5', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fill: '#b5b5b5', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value: number, name: string) => [
                      `${value.toFixed(1)}%`,
                      name === 'openRate' ? 'Open Rate' : 'Click Rate',
                    ]}
                  />
                  <Bar dataKey="openRate" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={30} name="Open Rate" />
                  <Bar dataKey="clickRate" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={30} name="Click Rate" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <TrendingUp className="w-12 h-12 mb-3 opacity-30" />
                <p>No performance data yet</p>
                <p className="text-xs mt-1">Send campaigns to see performance metrics</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Separator className="opacity-20" />

      {/* Tabs for detailed management */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <Mail className="w-4 h-4 mr-2" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="automations">
            <Zap className="w-4 h-4 mr-2" />
            Automations
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Open Rate</TableHead>
                  <TableHead>Click Rate</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns
                  .filter(
                    (c) =>
                      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      c.subject.toLowerCase().includes(searchQuery.toLowerCase()),
                  )
                  .map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[300px]">
                            {campaign.subject}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>{campaign.sentCount.toLocaleString()}</TableCell>
                      <TableCell>
                        <span
                          className={
                            campaign.openRate >= 25
                              ? 'text-green-400'
                              : campaign.openRate >= 15
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }
                        >
                          {campaign.openRate.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            campaign.clickRate >= 5
                              ? 'text-green-400'
                              : campaign.clickRate >= 2
                              ? 'text-yellow-400'
                              : 'text-muted-foreground'
                          }
                        >
                          {campaign.clickRate.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(campaign.createdAt).toLocaleDateString('en-ZA')}
                      </TableCell>
                    </TableRow>
                  ))}
                {campaigns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No campaigns yet. Send a bulk email to create your first campaign.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">
              Create and manage reusable email templates for campaigns and automations
            </p>
            <Button
              data-testid="admin-email-new-template-button"
              onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({ name: '', subject: '', content: '', category: 'custom' });
                setShowTemplateDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="hover:border-[#FDB022]/30 transition-colors h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <Badge variant="outline" className="text-xs capitalize">
                        {template.category}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{template.subject}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground line-clamp-3 mb-4">
                      {template.content.replace(/<[^>]+>/g, '').substring(0, 200)}...
                    </p>
                    {template.variables.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {template.variables.map((v) => (
                          <Badge key={v} variant="secondary" className="text-[10px] px-1.5">
                            {`{{${v}}}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        {new Date(template.updatedAt || template.createdAt).toLocaleDateString('en-ZA')}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid="admin-email-edit-template-button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateForm({
                              name: template.name,
                              subject: template.subject,
                              content: template.content,
                              category: template.category,
                            });
                            setShowTemplateDialog(true);
                          }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          data-testid="admin-email-delete-template-button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            {templates.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No templates yet</p>
                <p className="text-xs mt-1">Create your first email template to get started</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">
              View and manage automated email workflows and drip sequences
            </p>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Automation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map((automation) => (
                  <TableRow key={automation.id}>
                    <TableCell>
                      <span className="font-medium">{automation.name}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">
                        {automation.type?.replace(/-/g, ' ') || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {automation.trigger?.event?.replace(/-/g, ' ') || 'Manual'}
                      {automation.trigger?.daysDelay
                        ? ` (+${automation.trigger.daysDelay}d)`
                        : ''}
                    </TableCell>
                    <TableCell>{getStatusBadge(automation.status)}</TableCell>
                    <TableCell>{automation.sequence?.length || 0} steps</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={automation.status === 'active'}
                          onCheckedChange={() => handleToggleAutomation(automation)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {automations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No automations configured. Set them up in the Marketing Hub.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Email Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Bulk Email</DialogTitle>
            <DialogDescription>
              Send an email to a group of users on the platform
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recipients</Label>
              <Select
                value={bulkRecipients}
                onValueChange={(v) => setBulkRecipients(v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    All Users ({recipientCounts.all})
                  </SelectItem>
                  <SelectItem value="agents">
                    Agents ({recipientCounts.agents})
                  </SelectItem>
                  <SelectItem value="sellers">
                    Sellers ({recipientCounts.sellers})
                  </SelectItem>
                  <SelectItem value="marketing">
                    Marketing Team ({recipientCounts.marketing})
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {templates.length > 0 && (
              <div className="space-y-2">
                <Label>Use Template (Optional)</Label>
                <Select value={bulkTemplateId || '__none__'} onValueChange={(v) => setBulkTemplateId(v === '__none__' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template or write custom content" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No template</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Subject Line *</Label>
              <Input
                value={bulkSubject}
                onChange={(e) => setBulkSubject(e.target.value)}
                placeholder="Enter email subject..."
              />
            </div>

            <div className="space-y-2">
              <Label>Email Content {bulkTemplateId ? '(Optional with template)' : '*'}</Label>
              <Textarea
                value={bulkContent}
                onChange={(e) => setBulkContent(e.target.value)}
                placeholder="Write your email content here... Use {{firstName}}, {{propertyName}} etc. for variables"
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Available variables: {'{{firstName}}'}, {'{{lastName}}'}, {'{{email}}'}, {'{{role}}'}
              </p>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Will be sent to{' '}
              <span className="font-medium text-foreground">
                {getRecipientEmails().length}
              </span>{' '}
              recipients
            </p>
            <div className="flex items-center gap-2">
              <Button data-testid="admin-email-bulk-cancel-button" variant="outline" onClick={() => setShowBulkDialog(false)}>
                Cancel
              </Button>
              <Button data-testid="admin-email-bulk-send-button" onClick={handleSendBulkEmail} disabled={isSending}>
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Email Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? 'Update the email template details'
                : 'Create a reusable email template for campaigns'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Welcome Email"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={templateForm.category}
                  onValueChange={(v) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      category: v as EmailTemplate['category'],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="welcome">Welcome</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                    <SelectItem value="property-alert">Property Alert</SelectItem>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                    <SelectItem value="promotional">Promotional</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subject Line *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, subject: e.target.value }))
                }
                placeholder="Email subject line..."
              />
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={templateForm.content}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, content: e.target.value }))
                }
                placeholder="Write your email content... Use {{variableName}} for dynamic content"
                rows={12}
              />
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-end gap-2">
            <Button data-testid="admin-email-template-cancel-button" variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button data-testid="admin-email-template-save-button" onClick={handleSaveTemplate}>
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}